import React, { Fragment } from 'react'
import { Row, Box } from '../../legacy-design-system/form'
import { TextInput } from '../../legacy-design-system/form-text'
import { SyslogSeverity, SyslogProtocol, SyslogSettingsFormState } from '.'
import {
  SingleSelectComboboxString,
  SingleSelectComboboxNumber
} from '../../legacy-design-system/combobox'
import {
  SyslogAttackStatus,
  getAttackStatusFromSyslogAttackStatus
} from './syslog-attack-status'
import { css } from '@emotion/css'
import { AttackStatusBadge } from '../badge'
import { Label } from '../../legacy-design-system/form-label'
import { GetFormFieldPropsFn } from '../../legacy-design-system/form/form-validator'
import { ContrastFormattedMessage } from '../contrast-formatted-message'

const testIdPrefix = 'syslog-settings-form'
export const syslogSettingsFormTestIds = {
  host: `${testIdPrefix}-host`,
  port: `${testIdPrefix}-port`,
  protocol: `${testIdPrefix}-protocol`,
  facilityCode: `${testIdPrefix}-facility-code`,
  severity: `${testIdPrefix}-severity`
}

export function SyslogSettingsForm({
  viewOnly = false,
  state,
  onChange,
  getFormFieldProps
}: {
  readonly viewOnly?: boolean
  readonly state: SyslogSettingsFormState
  readonly onChange: (value: SyslogSettingsFormState) => void
  readonly getFormFieldProps: GetFormFieldPropsFn
}) {
  const { host, port, protocol, facilityCode, severityByAttackStatus } = state
  const onChangeFactory =
    (
      key:
        | 'port'
        | 'host'
        | 'protocol'
        | 'facilityCode'
        | 'severityByAttackStatus'
    ) =>
    (value) => {
      onChange({
        port,
        host,
        protocol,
        facilityCode,
        severityByAttackStatus,
        [key]: value
      })
    }
  const onSeverityChange = (
    syslogAttackStatus: SyslogAttackStatus,
    severity: SyslogSeverity
  ) => {
    severityByAttackStatus[syslogAttackStatus] = severity
    onChangeFactory('severityByAttackStatus')(severityByAttackStatus)
  }
  return (
    <Fragment>
      <Row>
        <Box basis={5}>
          <HostInput
            disabled={viewOnly}
            value={host}
            onChange={onChangeFactory('host')}
            getFormFieldProps={getFormFieldProps}
          />
        </Box>
        {/* TODO: Uncomment to enable Secure Syslog for TS-4835
        <Box basis={3}>
          <ProtocolSelect
            disabled={viewOnly}
            value={protocol}
            onChange={onChangeFactory('protocol')}
            getFormFieldProps={getFormFieldProps}
          />
        </Box> */}
        <Box basis={3}>
          <PortInput
            disabled={viewOnly}
            protocol={protocol}
            value={port}
            onChange={onChangeFactory('port')}
            getFormFieldProps={getFormFieldProps}
          />
        </Box>
      </Row>
      <Row>
        <Box basis={5}>
          <FacilityCodeSelect
            disabled={viewOnly}
            value={facilityCode}
            onChange={onChangeFactory('facilityCode')}
            getFormFieldProps={getFormFieldProps}
          />
        </Box>
      </Row>
      <Row>
        <Box basis={5}>
          <Row>
            <Box
              basis={6}
              className={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: center;
              `}
            >
              <ContrastFormattedMessage
                id="detail_syslogSettings_attackStatusResult"
                children={(msg) => <Label>{msg}</Label>}
              />
              {Object.keys(SyslogAttackStatus).map(
                (syslogAttackStatus: SyslogAttackStatus) => {
                  return (
                    <Row
                      key={syslogAttackStatus}
                      className={css`
                        align-items: center;
                        height: 50px;
                      `}
                    >
                      <Box basis={6}>
                        <AttackStatusBadge
                          status={
                            getAttackStatusFromSyslogAttackStatus[
                              syslogAttackStatus
                            ]
                          }
                        />
                      </Box>
                    </Row>
                  )
                }
              )}
            </Box>
            <Box
              basis={6}
              className={css`
                flex: 4;
              `}
            >
              <ContrastFormattedMessage
                id="detail_syslogSettings_messageSeverity"
                children={(msg) => <Label>{msg}</Label>}
              />
              {Object.keys(SyslogAttackStatus).map(
                (syslogAttackStatus: SyslogAttackStatus) => {
                  const id = `${syslogSettingsFormTestIds.severity}-${syslogAttackStatus}`
                  return (
                    <Row
                      key={syslogAttackStatus}
                      className={css`
                        align-items: center;
                      `}
                    >
                      <Box
                        basis={6}
                        className={css`
                          flex: 4;
                        `}
                      >
                        <SingleSelectComboboxString<SyslogSeverity>
                          disabled={viewOnly}
                          inputDataTestId={id}
                          comboboxMenuItemToTestID={(item) => `${id}-${item}`}
                          items={[
                            {
                              items: syslogSeverities.map(({ value }) => value)
                            }
                          ]}
                          itemToString={severityItemToString}
                          value={severityByAttackStatus[syslogAttackStatus]}
                          onChange={(item) =>
                            onSeverityChange(syslogAttackStatus, item)
                          }
                          {...getFormFieldProps({ id })}
                        />
                      </Box>
                    </Row>
                  )
                }
              )}
            </Box>
          </Row>
        </Box>
      </Row>
    </Fragment>
  )
}

interface Common<T> {
  readonly disabled?: boolean
  readonly value: T
  readonly onChange: (value: T) => void
  readonly getFormFieldProps: GetFormFieldPropsFn
}

// Note: these are ordered numerically, by code
const syslogSeverities: Array<{
  readonly code: 1 | 2 | 3 | 4 | 5 | 6 | 7
  readonly value: SyslogSeverity
}> = [
  {
    code: 1,
    value: 'ALERT'
  },
  {
    code: 2,
    value: 'CRITICAL'
  },
  {
    code: 3,
    value: 'ERROR'
  },
  {
    code: 4,
    value: 'WARNING'
  },
  {
    code: 5,
    value: 'NOTICE'
  },
  {
    code: 6,
    value: 'INFO'
  },
  {
    code: 7,
    value: 'DEBUG'
  }
]

export function severityItemToString(severity: SyslogSeverity) {
  switch (severity) {
    case 'ALERT':
      return '1 - Alert'
    case 'CRITICAL':
      return '2 - Critical'
    case 'ERROR':
      return '3 - Error'
    case 'WARNING':
      return '4 - Warning'
    case 'NOTICE':
      return '5 - Notice'
    case 'INFO':
      return '6 - Info'
    case 'DEBUG':
      return '7 - Debug'
  }
}

function HostInput({
  disabled,
  value,
  onChange,
  getFormFieldProps
}: Common<undefined | string>) {
  const id = syslogSettingsFormTestIds.host
  return (
    <ContrastFormattedMessage
      id="detail_syslogSettings_exampleHostValues"
      values={{ example0: 'mydomain.com', example1: '38.124.154.50' }}
    >
      {(ipExample) => (
        <ContrastFormattedMessage id="detail_syslogSettings_syslogServerHost">
          {(ipLabel) => (
            <TextInput
              disabled={disabled}
              placeholder={ipExample}
              required
              showRequiredIndicator={false}
              data-testid={id}
              label={ipLabel}
              value={value}
              onChange={onChange}
              {...getFormFieldProps({ id })}
            />
          )}
        </ContrastFormattedMessage>
      )}
    </ContrastFormattedMessage>
  )
}

// TODO: Uncomment to enable Secure Syslog for TS-4835
// const protocolDisplayValuess: Record<SyslogProtocol, string> = {
//   UDP: 'UDP',
//   TCP: 'TCP',
//   TCP_AND_TLS: 'TCP + TLS'
// }

// TODO: Uncomment to enable Secure Syslog for TS-4835
// function ProtocolSelect({
//   disabled,
//   value,
//   onChange,
//   getFormFieldProps
// }: Common<SyslogProtocol>) {
//   const id = syslogSettingsFormTestIds.protocol
//   return (
//     <ContrastFormattedMessage id="title_protocol">
//       {protocolTitle => (
//         <SingleSelectComboboxString<SyslogProtocol>
//           disabled={disabled}
//           inputDataTestId={id}
//           comboboxMenuItemToTestID={item => `${id}-${item}`}
//           label={protocolTitle}
//           items={[
//             {
//               items: ['UDP', 'TCP', 'TCP_AND_TLS']
//             }
//           ]}
//           value={value}
//           onChange={onChange}
//           itemToString={item => protocolDisplayValuess[item]}
//           {...getFormFieldProps({ id })}
//         />
//       )}
//     </FormattedMessage>
//   )
// }

const protocolDefaultPort: Record<SyslogProtocol, number> = {
  UDP: 514,
  TCP: 601,
  TCP_AND_TLS: 6514
}

function PortInput({
  disabled,
  protocol,
  value,
  onChange,
  getFormFieldProps
}: Common<undefined | number> & {
  readonly protocol: SyslogProtocol
}) {
  const id = syslogSettingsFormTestIds.port
  return (
    <ContrastFormattedMessage
      id="variable_servers_exampleValue_react"
      values={{ exampleValue: protocolDefaultPort[protocol] }}
    >
      {(portExample) => (
        <ContrastFormattedMessage id="title_port">
          {(portLabel) => (
            <TextInput
              disabled={disabled}
              data-testid={id}
              placeholder={portExample}
              required
              showRequiredIndicator={false}
              type="number"
              min={0}
              max={65535}
              step={1}
              label={portLabel}
              value={value}
              onChange={(value) =>
                onChange(!!value ? parseInt(value, 10) : value)
              }
              {...getFormFieldProps({ id })}
            />
          )}
        </ContrastFormattedMessage>
      )}
    </ContrastFormattedMessage>
  )
}

const facilityCodes = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17, 18, 19, 20, 21, 22, 23
]

const facilityCodeToDisplayString = {
  0: 'Kernel Messages',
  1: 'User-Level Messages',
  2: 'Mail System',
  3: 'System Daemons',
  4: 'Security/Authorization Messages',
  5: 'Messages Generated Internally by syslogd',
  6: 'Line Printer Subsystem',
  7: 'Network News Subsystem',
  8: 'UUCP Subsystem',
  9: 'Clock Daemon',
  10: 'Security/Authorization Messages',
  11: 'FTP Daemon',
  16: 'Local Use 0',
  17: 'Local Use 1',
  18: 'Local Use 2',
  19: 'Local Use 3',
  20: 'Local Use 4',
  21: 'Local Use 5',
  22: 'Local Use 6',
  23: 'Local Use 7'
}

export const facilityCodeItemToString = (facilityCode: number) =>
  `${facilityCode} - ${facilityCodeToDisplayString[facilityCode]}`

function FacilityCodeSelect({
  disabled,
  value,
  onChange,
  getFormFieldProps
}: Common<number>) {
  const id = syslogSettingsFormTestIds.facilityCode
  return (
    <ContrastFormattedMessage id="title_syslogFacility">
      {(facilityLabel) => (
        <SingleSelectComboboxNumber
          disabled={disabled}
          inputDataTestId={id}
          comboboxMenuItemToTestID={(item) => `${id}-${item}`}
          label={facilityLabel}
          items={[
            {
              items: facilityCodes
            }
          ]}
          value={value}
          onChange={onChange}
          itemToString={facilityCodeItemToString}
          {...getFormFieldProps({ id })}
        />
      )}
    </ContrastFormattedMessage>
  )
}
